import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import Planet from './Planet';
import Stars from './Stars';
import Constellations from './Constellations';
import PlanetMarker from './PlanetMarker';
import CameraController from './CameraController';
import { sections } from '../data/portfolioData';

interface PlanetSceneProps {
  selectedIndex: number | null;
  onSelectSection: (index: number) => void;
  isZoomed: boolean;
}

// Offsets the idle view so the planet sits right-of-center on wide screens,
// leaving room for the hero column on the left.
function SceneRig({ selectedIndex, isZoomed }: { selectedIndex: number | null; isZoomed: boolean }) {
  const width = useThree((s) => s.size.width);
  const idleLookX = width >= 900 ? -2.4 : 0;
  const selectedSection = selectedIndex !== null ? sections[selectedIndex] : null;

  if (isZoomed || selectedIndex !== null) {
    return (
      <CameraController
        target={selectedSection?.position || null}
        isZoomed={isZoomed}
        idleLookX={idleLookX}
      />
    );
  }

  return (
    <OrbitControls
      enablePan={false}
      enableZoom={true}
      minDistance={5}
      maxDistance={15}
      autoRotate
      autoRotateSpeed={0.3}
      dampingFactor={0.05}
      enableDamping
      target={[idleLookX, 0, 0]}
    />
  );
}

export default function PlanetScene({ selectedIndex, onSelectSection, isZoomed }: PlanetSceneProps) {
  return (
    <Canvas
      camera={{ position: [-2.4, 0.5, 8], fov: 45 }}
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%' }}
      gl={{ antialias: true, alpha: true }}
    >
      <color attach="background" args={['#050510']} />

      <ambientLight intensity={0.15} />
      <directionalLight position={[5, 3, 5]} intensity={0.4} color="#4060ff" />
      <directionalLight position={[-5, -2, -3]} intensity={0.2} color="#8040ff" />
      <pointLight position={[0, 0, 5]} intensity={0.3} color="#00f0ff" />

      <SceneRig selectedIndex={selectedIndex} isZoomed={isZoomed} />

      <Stars />
      <Constellations />
      <Planet isZoomed={isZoomed} />

      {sections.map((section, i) => (
        <PlanetMarker
          key={section.id}
          section={section}
          isSelected={selectedIndex === i}
          onClick={() => onSelectSection(i)}
        />
      ))}
    </Canvas>
  );
}
